import _ from 'underscore';
// eslint-disable-next-line max-len
// legacy$
import $ from 'jquery';
import { DeterministicWrapperView } from '@bingads-webui/deterministic-wrapper-view';
import { queryify } from '@bingads-webui/url-util';
import { constants } from '@bingads-webui/projection-grid-toolbar';
import { PopoverView, positions } from '@bingads-webui/popover';
import { setUpErrorHandling } from '@bingads-webui-campaign/summary-grid-helper';
import { merge } from '@bingads-webui-universal/primitive-utilities';
import bidWarningContentTemplate from '../../template/bid-warning-content.pug';
import bidWarningTitleTemplate from '../../template/bid-warning-title.pug';
import { ProductGroupBidLandscapePopupFactory } from './bid-landscape-popup';
import { ProductGroupsSelectorDialog } from './product-groups-selector-dialog';
import { ProductGroupPathUtil } from './product-groups-path-util';
import ProductGroupsDetailView from './product-groups-detail-view';
import { PRODUCT_OFFER_DETAIL_SHOW_DELAY, PRODUCT_OFFER_DETAIL_CLOSE_DELAY, FETCH_OFFLINE_STATUS_DELAY, DEFAULT_CURRENT_PAGE, MAX_PAGE_SIZE, productGroupLevel } from '../../constant';
import { SingleViewWrapper } from './single-view-wrapper';

class AbstractProductGroupsView extends DeterministicWrapperView {
  viewFactory({
    deps,
    campaign,
    adgroup,
    filterService,
    level,
    gridParameters,
    callbacks,
    preferenceConfig,
  }) {
    this.removed = false;
    this.deps = deps;
    this.gridParameters = gridParameters;
    this.callbacks = callbacks;
    this.campaignId = _.result(campaign, 'Id');
    this.campaignSubType = _.result(campaign, 'CampaignSubType');
    this.adgroupId = _.result(adgroup, 'Id');
    this.filterService = filterService;
    this.level = level;
    this.productGroupPathUtil = new ProductGroupPathUtil({ deps });
    if (adgroup) {
      this.setting = _.first(adgroup.Settings) || {};
      // Default BidOption is BidValue
      this.isBidValueOption = _.result(this.setting, 'BidOption', 0) === 0;
      this.isBidBoostOption = !this.isBidValueOption;
      this.defaultBid = this.isBidValueOption ? _.result(adgroup, 'Bid') : this.setting.BidBoostValue;
    } else {
      this.isBidValueOption = true;
      this.isBidBoostOption = _.result(this.deps.config.get('Permissions'), 'IsCPGForBSCEnabled');
    }

    setUpErrorHandling({
      gridView: this,
      scope: {
        campaignId: this.campaignId,
        adgroupId: this.adgroupId,
      },
      dataSource: this.gridParameters.dataSource,
      i18n: this.deps.i18n,
    });

    this.refreshGrid = () => {
      this.deps.cacheManager.invalidate('ProductGroup');
      this.grid.gridView.refresh(true);
    };
    this.productOfferOpenTimer = undefined;
    this.productOfferCloseTimer = undefined;
    this.fetchOfflineStatusTimer = undefined;

    const config = merge(this.getGridConfig(), preferenceConfig);
    this.initializeGrid(config);
    this.grid.columnChooser.columnChooserAgent.onRecordRemoved(() => {
      callbacks.onColumnChooserMenuChanged(this.isTreeView);
    });
    this.grid.columnChooser.columnChooserAgent.onRecordSaved(() => {
      callbacks.onColumnChooserMenuChanged(this.isTreeView);
    });
    this.grid.columnChooser.columnChooserAgent.onSelectedRecordChanged(() => {
      callbacks.onColumnChooserMenuChanged(this.isTreeView);
    });
    this.grid.columnChooser.columnChooserAgent.onSelectedColumnsChanged((names) => {
      callbacks.onSelectedColumnsChanged(names);
    });
    return this.grid.gridView;
  }

  getAttributeDisplayName = (gridView, row) => {
    const parentId = row.ParentId;
    const parentRow = gridView.itemWithKey(parentId);

    if (_.isEmpty(row.Condition.Operand)) {
      return this.deps.i18n.getString('ShoppingCampaign_AllProducts');
    } else if (_.isEmpty(row.Condition.Attribute)) {
      if (!parentRow.Condition.Attribute && parentRow.Condition.Operand) {
        // Parent node is everything else
        return this.deps.i18n.getString('ProductGroupsGrid_EverythingElse');
      }
      // Parent node is not everything else
      const parentAttribute = parentRow.Condition.Attribute || this.deps.i18n.getString('ShoppingCampaign_AllProducts');
      return this.deps.i18n.getString('ProductGroupsGrid_EverythingElseIn').replace('{0}', parentAttribute);
    }

    return row.Condition.Attribute;
  };

  getFullConditionPath = (gridView, row) => {
    const path = [];

    [row.Condition, ...row.ParentConditions].forEach((condition) => {
      path.push(_.defaults({
        Attribute: this.getAttributeDisplayName(gridView, this.productGroupPathUtil),
      }, condition));
    });

    return path;
  }

  getPartitionFullPath = (gridView, rowId) => {
    const path = [];
    let row = gridView.itemWithKey(rowId);
    let parentId = row.ParentId;

    path.push(_.defaults({
      Attribute: this.getAttributeDisplayName(gridView, row),
    }, row.Condition));

    while (parentId) {
      row = gridView.itemWithKey(parentId);
      path.unshift(_.defaults({
        Attribute: this.getAttributeDisplayName(gridView, row),
      }, row.Condition));
      parentId = row.ParentId;
    }
    return path;
  };

  expandToolBar = (view, productGroupsView) => {
    const toolbarEditor = productGroupsView.grid.toolbar.gridToolbarGroup
      .toolbarWithName(constants.DEFAULT_TOOLBAR_NAME);

    toolbarEditor.overrideWith(view);
    const closeView = () => {
      toolbarEditor.restoreToolbar();
      view.remove();
    };

    view.on('refresh', () => {
      productGroupsView.refreshGrid();
    });

    view.on('hide', () => {
      closeView(view);

      // collapse Edit menu after restore toolbar
      $('.toolbar .dropdown.open').removeClass('open');
    });

    view.on('edit-bid-errors', (errors) => {
      productGroupsView.grid.inlineError.setErrors(errors);
      closeView(view);
    });
  };

  events() {
    return {
      'mouseenter .product-group-name-container': 'onMouseEnterShowProductDetail',
      'mouseleave .product-group-name-container': 'onMouseLeaveShowProductDetail',
      'click .show-product-selector': 'showProductGroupSelector',
      'click .bidlandscape_icon': 'showBidLandscape',
      'click .show-product-warning': 'showProductWarning',
    };
  }

  onMouseEnterShowProductDetail(e) {
    clearTimeout(this.productOfferCloseTimer);
    this.productOfferOpenTimer = setTimeout(() => {
      this.showProductDetails(e);
    }, PRODUCT_OFFER_DETAIL_SHOW_DELAY);
  }

  onMouseLeaveShowProductDetail() {
    clearTimeout(this.productOfferOpenTimer);
    this.productOfferCloseTimer = setTimeout(() => {
      this.removeHoverProductDetails();
    }, PRODUCT_OFFER_DETAIL_CLOSE_DELAY);
  }

  initializeGrid(config) {
    const pGridFactory = this.initializeGridFactory();

    this.grid = pGridFactory.create(config);

    this.grid.gridView.on('forceRefresh', this.refreshGrid);

    this.grid.gridView.on('didInlineEdit', this.inlineEditSuccessCallback, this);

    this.grid.gridView.on('willReload', () => {
      this.grid.inlineError.clearErrors();

      const { options } = this.grid.gridView.get('dataSource');

      if (options.forceRefresh) {
        this.grid.gridView.deselectAll();
      }
    });

    const { i18n, pageContext } = this.deps;
    const processingDataMessage = i18n.getString('ProductGroupsGrid_ProcessingDataMessage');
    const dataReadyMessage = i18n.getString('ProductGroupsGrid_DataReadyMessage');
    let tooMuchProductOfferMessage = i18n.getString('ProductGroupsGrid_TooMuchProductOfferMessage_AccountLevel');
    if (pageContext.CurrentAdGroup != null) {
      tooMuchProductOfferMessage = i18n.getString('ProductGroupsGrid_TooMuchProductOfferMessage_AdGroupLevel');
    } else if (pageContext.CurrentCampaign != null) {
      tooMuchProductOfferMessage = i18n.getString('ProductGroupsGrid_TooMuchProductOfferMessage_CampaignLevel');
    }

    this.grid.gridView.on('didReload', () => {
      if (this.removed) {
        return;
      }
      let url;
      switch (this.level) {
        case productGroupLevel.AdGroup:
          url = '/ODataApi/Campaign/V2/Customers(:customer_id)/Accounts(:account_id)/Campaigns(:campaign_id)/AdGroups(:adgroup_id)/ProductGroups/Default.PollOfflineTaskExecutionStatus';
          break;
        case productGroupLevel.Campaign:
          url = '/ODataApi/Campaign/V2/Customers(:customer_id)/Accounts(:account_id)/Campaigns(:campaign_id)/ProductGroups/Default.PollOfflineTaskExecutionStatus';
          break;
        case productGroupLevel.Account:
        default:
          url = '/ODataApi/Campaign/V2/Customers(:customer_id)/Accounts(:account_id)/ProductGroups/Default.PollOfflineTaskExecutionStatus';
          break;
      }

      const data = this.grid.gridView.dataSource.rawData;
      clearInterval(this.fetchOfflineStatusTimer);
      this.fetchOfflineStatusTimer = undefined;
      const productGroupBIDataLoadStatus = data && data['@ns.productGroupBIDataLoadStatus'];
      if (productGroupBIDataLoadStatus === 'OfferCountLimitExceed') { // for the scenario that product offers more than 5 million
        this.callbacks.onShowTooMuchProductOffer(tooMuchProductOfferMessage);
      } else if (productGroupBIDataLoadStatus === 'OutOfDateOfferCountLimitExceed') { // for the normal offline status scenario
        this.callbacks.onMessageBar(processingDataMessage);
        this.fetchOfflineStatusTimer = setInterval(() => {
          this.deps.odata.get(url).then((response) => {
            if (response.value && this.fetchOfflineStatusTimer) {
              clearInterval(this.fetchOfflineStatusTimer);
              this.fetchOfflineStatusTimer = undefined;
              this.callbacks.onPopupMessage(dataReadyMessage);
            }
          });
        }, FETCH_OFFLINE_STATUS_DELAY);
      }
    });

    this.grid.gridView.on('didUpdate', (changes) => {
      if (this.grid.gridView.countRows === 0) {
        // `.create-root-node` doesn't exist in current area.
        // we need use JQuery to hook event for root node creating
        $('.create-root-node').click(() => {
          this.createRootNode();
        });
      }
      if (_.has(changes, 'sortableHeader')) {
        const orderby = _.result(changes.query, 'orderby') || _.result(this.grid.gridView.get('query'), 'orderby');
        const { sortableHeader } = changes;

        this.deps.preferenceService.setAtUserLevel({ Name: `WebApp.CampaignUI.${config.name}`, Data: { sortInfo: { orderby, sortableHeader } } });
      }
    });

    this.bidLandscapePopupFactory = new ProductGroupBidLandscapePopupFactory({
      deps: this.deps,
      view: this,
    });
  }

  showProductWarning(event) {
    const IsBidLessThan5CentsInShoppingCampaignEnabled = _.result(this.deps.config.get('Permissions'), 'IsBidLessThan5CentsInShoppingCampaignEnabled');
    const bidRangeKey = IsBidLessThan5CentsInShoppingCampaignEnabled ? 'ProductGroupBidRange' : 'KeywordBidRange';
    const MinimumBid = _.result(this.deps.config.get(bidRangeKey), 'FloorValue');
    this.warningDetailPopover = new this.deps.PopoverView({
      target: $(event.target),
      className: 'product-group-popover-warning',
      position: this.deps.positions.relativeToTableRow({
        marginBottom: $('footer > .navbar-footer').height(),
      }),
      eventActions: {
        'click .set-bid': () => {
          this.showBidEditorForm(event);
        },
      },
    }).setContent(new this.deps.contents.TitledContent()
      .setBody({
        html: bidWarningContentTemplate({
          i18n: this.deps.i18n,
          reason: this.deps.i18n.getString('ProductGroupsGrid_BidError_PopupContent', {
            bidAmount: MinimumBid,
          }),
        }),
      })
      .setTitle({
        html: bidWarningTitleTemplate({ i18n: this.deps.i18n }),
      }));
  }

  showProductDetails(e) {
    const item = this.grid.gridView.itemOfElement(e.target);
    if (!item || item.Id === this.currentHoverItemId) {
      return;
    }
    this.currentHoverItemId = item.Id;
    const conditions = item.ParentConditions ?
      [...item.ParentConditions, item.Condition] : [item.Condition];
    const partitionFullPath =
      this.productGroupPathUtil.getFullPath(conditions);
    if (!this.ProductDetailPopoverView) {
      this.ProductDetailPopoverView = new PopoverView({
        target: $(e.target),
        position: positions.relativeToTableRow({
          marginBottom: $('footer > .navbar-footer').height(),
        }),
      });
      this.ProductDetailPopoverView.on('remove', () => {
        this.ProductDetailPopoverView = null;
        this.currentHoverItemId = null;
      });
    } else {
      this.ProductDetailPopoverView.setPositionByTarget($(e.target));
    }
    this.ProductDetailPopoverView.setContent(new SingleViewWrapper({
      view: new ProductGroupsDetailView({
        deps: this.deps,
        grid: this,
        campaignId: item.CampaignId,
        adGroupId: item.AdGroupId,
        productPartitionId: item.Id,
        partitionFullPath,
        isEverythingElseRow:
          _.isEmpty(item.Condition.Attribute) && !_.isEmpty(item.Condition.Operand),
        parentView: this.ProductDetailPopoverView,
      }),
      rendered: false,
    }));
  }

  removeHoverProductDetails() {
    if (this.ProductDetailPopoverView) {
      this.ProductDetailPopoverView.remove();
    }
  }

  refreshColumnChooserMenu() {
    this.grid.columnChooser.refreshMenu();
  }

  updateSelectedColumns(names) {
    this.grid.columnChooser.setSelectedColumns(names);
  }

  showProductGroupSelector(event) {
    const item = this.grid.gridView.itemOfElement(event.target);
    if (!item) {
      return;
    }
    const partitionFullPath = this.getPartitionFullPath(this.grid.gridView, item.Id);
    const children = _.map(item.childrenKeys, key => this.grid.gridView.itemWithKey(key));

    this.selectorView = new ProductGroupsSelectorDialog({
      deps: this.deps,
      currentPartition: item,
      displayName: this.getAttributeDisplayName(this.grid.gridView, item),
      children,
      partitionFullPath,
      defaultBid: this.defaultBid,
      campaignSubType: this.campaignSubType,
      isBidValueOption: this.isBidValueOption,
    }).render();

    this.selectorView.on('save', ({ expandedNodeDelta }) => {
      this.selectorView.remove();
      this.refreshGrid();
      this.grid.gridView.deselectAll();
      this.callbacks.markNeedSync();

      if (expandedNodeDelta) {
        // expand the newly added subdivision node
        const expandedNodeIdsCur =
          _.result(this.grid.gridView.get('tree-toggle'), 'expandedNodeIds', []);

        this.grid.tree.setExpandedNodeIds([...expandedNodeIdsCur, expandedNodeDelta]);
      }
    });
  }

  showBidLandscape(event) {
    event.stopPropagation();
    const item = this.grid.gridView.itemOfElement(event.target);
    if (!item) {
      return;
    }

    this.bidLandscapePopupFactory.showBidLandscapePopup({
      event,
      item: _.defaults({
        attributeDisplayName: this.getAttributeDisplayName(this.grid.gridView, item),
      }, item),
    });
    this.on('saveLandscape', this.refreshGrid);
  }

  createRootNode() {
    const bidRow = this.isBidValueOption ?
      { 'Added[0].BidValue': this.defaultBid } :
      { 'Added[0].BidBoost': this.defaultBid };

    $.advertiser.ajaxPost({
      url: queryify('/campaign/ProductGroups/UpdateSubdivision', {
        cid: this.deps.state.get('customer_id'),
        uid: this.deps.state.get('user_id'),
        aid: this.deps.state.get('account_id'),
        cmpid: this.deps.state.get('campaign_id'),
        adgid: this.deps.state.get('adgroup_id'),
        __adcenterAJAX: true,
      }),
      data: _.extend({
        'Added[0].DimensionType': '',
        'Added[0].DimensionValue': '',
        'Added[0].Id': null,
        'Added[0].ParentId': null,
      }, bidRow),
      onJsonError: ({ Errors } = {}) => {
        window.App.views.alertCenter.toastError(_.pluck(Errors, 'Message'));
      },
      onSuccess: (data) => {
        if (!_.result(data, 'Errors')) {
          this.refreshGrid();
        }
      },
    });
  }

  showBidEditorForm(event) {
    this.warningDetailPopover.remove();
    this.warningDetailPopover = null;

    // first select the current product group
    this.grid.gridView.selectRow(this.grid.gridView.keyOfElement(event.target));

    this.editBids();
  }

  editBids(bidType) {
    const selectedItems = this.grid.gridView.selectedItems();

    if (_.isEmpty(selectedItems)) {
      this.deps.popup.alert(this.deps.i18n.getString('Grid_RowSelectionRequired'));
      return;
    }
    const BulkEditView = this.deps.bulkEditView;
    const BulkEditModel = this.deps.bulkEditModel;

    this.editBidsView = new BulkEditView({
      model: new BulkEditModel({
        // selectedEntityCount: getGridSelectionCount(),
        Entity: 'ProductGroup',
      }),
      grid: {
        gridView: this.grid.gridView,
        pgridPlugins: {
          pagination: !this.isTreeView ? this.grid.pagination : undefined,
          crossPageSelectAll: !this.isTreeView ? this.grid.crossPageSelectAll : undefined,
          // filter,
        },
        bidType,
        // getPageSize and getCurrentPage are used for bulk edit on Tree view,
        // and on Flatten view Pagination plugin provides this info
        getPageSize: () => MAX_PAGE_SIZE,
        getCurrentPage: () => DEFAULT_CURRENT_PAGE,
        filterService: this.filterService,
      },
      deps: this.deps,
      isTreeView: this.isTreeView,
      defaultBid: this.defaultBid,
      entityType: 'ProductGroup',
      onOperationSuccess: () => {
        this.callbacks.markNeedSync();
        this.deps.cacheManager.invalidate('ProductGroup');
        this.grid.gridView.deselectAll();
      },
    }).render();

    this.expandToolBar(this.editBidsView, this);
  }

  inlineEditSuccessCallback() {
    this.callbacks.markNeedSync();
    this.refreshGrid();
    this.grid.gridView.deselectAll();
  }

  getToolBar() {
    return this.grid.toolbar.gridToolbarGroup.toolbars['default-toolbar'];
  }

  redirect(path) {
    window.location.href = path;
  }

  remove(...args) {
    this.removed = true;
    this.grid.gridView.remove();
    this.grid.columnChooser.remove();
    this.grid.toolbar.gridToolbarGroup.remove();

    if (!(_.isNull(this.editBidsView) || _.isUndefined(this.editBidsView))) {
      this.editBidsView.remove();
    }

    if (this.fetchOfflineStatusTimer) {
      clearInterval(this.fetchOfflineStatusTimer);
      this.fetchOfflineStatusTimer = undefined;
    }

    super.remove(...args);
  }
}

export default AbstractProductGroupsView;
